import React, { useState, useEffect, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { FormattedMessage, useIntl } from 'react-intl'
import { toAbsoluteUrl } from '../../../_metronic/helpers'
import './ModernFileViewer.css'

interface FileItem {
  url: string
  name: string
  extension: string
}

interface ImageLoadState {
  [key: string]: 'loading' | 'loaded' | 'error'
}

interface LoadProgress {
  [key: string]: number // Percentage 0-100
}

export const ModernFileViewer = () => {
  const [searchParams] = useSearchParams()
  const intl = useIntl()

  // Parse files from URL (support multiple files)
  const [files, setFiles] = useState<FileItem[]>([])
  const [currentIndex, setCurrentIndex] = useState(0)
  const [zoomLevel, setZoomLevel] = useState(1)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 })
  const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 })
  const [imageLoadStates, setImageLoadStates] = useState<ImageLoadState>({})
  const [retryCount, setRetryCount] = useState<{[key: string]: number}>({})
  const [loadProgress, setLoadProgress] = useState<LoadProgress>({})

  useEffect(() => {
    // Parse files from URL parameters
    const rawFileUrl = searchParams.get('file')
    const fileName = searchParams.get('fileName')
    const additionalFiles = searchParams.get('files') // JSON array of additional files

    const baseUrl = process.env.REACT_APP_API_URL || 'https://api.maarefati.com'

    const constructUrl = (url: string) => {
      if (!url) return ''
      if (url.startsWith('http')) return url
      if (url.startsWith('/')) return `${baseUrl}${url}`
      return `${baseUrl}/${url}`
    }

    const filesList: FileItem[] = []

    // Check if we have additional files (gallery mode)
    if (additionalFiles) {
      try {
        const parsed = JSON.parse(additionalFiles)
        console.log('📸 Gallery Mode - Files received:', parsed)
        console.log('📸 Number of files:', parsed.length)
        
        if (Array.isArray(parsed)) {
          // Use the files array entirely (don't add primary file separately to avoid duplication)
          parsed.forEach((file: any) => {
            const extension = file.name.split('.').pop()?.toLowerCase() || ''
            console.log('Adding file to gallery:', file.name, file.url)
            filesList.push({
              url: constructUrl(file.url),
              name: file.name,
              extension,
            })
          })
          
          console.log('📸 Total files in gallery:', filesList.length)
          
          // Find the index of the primary file to set as current
          if (fileName) {
            const primaryIndex = filesList.findIndex(f => f.name === fileName)
            console.log('📸 Starting at index:', primaryIndex, 'for file:', fileName)
            if (primaryIndex >= 0) {
              setCurrentIndex(primaryIndex)
            }
          }
        }
      } catch (e) {
        console.error('Error parsing additional files:', e)
        // Fallback: add primary file only
        if (rawFileUrl && fileName) {
          const extension = fileName.split('.').pop()?.toLowerCase() || ''
          filesList.push({
            url: constructUrl(rawFileUrl),
            name: fileName,
            extension,
          })
        }
      }
    } else {
      // Single file mode - add primary file only
      if (rawFileUrl && fileName) {
        const extension = fileName.split('.').pop()?.toLowerCase() || ''
        filesList.push({
          url: constructUrl(rawFileUrl),
          name: fileName,
          extension,
        })
      }
    }

    setFiles(filesList)
  }, [searchParams])

  const currentFile = files[currentIndex]

  // Zoom controls
  const zoomIn = useCallback(() => {
    setZoomLevel((prev) => Math.min(prev + 0.25, 3))
  }, [])

  const zoomOut = useCallback(() => {
    setZoomLevel((prev) => Math.max(prev - 0.25, 0.5))
  }, [])

  const resetZoom = useCallback(() => {
    setZoomLevel(1)
    setImagePosition({ x: 0, y: 0 })
  }, [])

  // Navigation
  const nextImage = useCallback(() => {
    if (currentIndex < files.length - 1) {
      setCurrentIndex(currentIndex + 1)
      resetZoom()
    }
  }, [currentIndex, files.length, resetZoom])

  const prevImage = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1)
      resetZoom()
    }
  }, [currentIndex, resetZoom])

  const goToImage = useCallback(
    (index: number) => {
      setCurrentIndex(index)
      resetZoom()
    },
    [resetZoom]
  )

  // Download
  const handleDownload = useCallback(() => {
    if (!currentFile) return

    fetch(currentFile.url)
      .then((response) => response.blob())
      .then((blob) => {
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.download = currentFile.name
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(link.href)
      })
      .catch((error) => {
        console.error('Error downloading the file:', error)
      })
  }, [currentFile])

  // Download all
  const handleDownloadAll = useCallback(() => {
    files.forEach((file, index) => {
      setTimeout(() => {
        fetch(file.url)
          .then((response) => response.blob())
          .then((blob) => {
            const link = document.createElement('a')
            link.href = URL.createObjectURL(blob)
            link.download = file.name
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            URL.revokeObjectURL(link.href)
          })
          .catch((error) => {
            console.error('Error downloading file:', error)
          })
      }, index * 500) // Stagger downloads
    })
  }, [files])

  // Fullscreen
  const toggleFullscreen = useCallback(() => {
    setIsFullscreen(!isFullscreen)
  }, [isFullscreen])

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowLeft':
          prevImage()
          break
        case 'ArrowRight':
          nextImage()
          break
        case '+':
        case '=':
          zoomIn()
          break
        case '-':
        case '_':
          zoomOut()
          break
        case '0':
          resetZoom()
          break
        case 'Escape':
          if (isFullscreen) setIsFullscreen(false)
          break
        case 'f':
        case 'F':
          toggleFullscreen()
          break
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [nextImage, prevImage, zoomIn, zoomOut, resetZoom, isFullscreen, toggleFullscreen])

  // Drag to pan when zoomed
  const handleMouseDown = (e: React.MouseEvent) => {
    if (zoomLevel > 1) {
      setIsDragging(true)
      setDragStart({ x: e.clientX - imagePosition.x, y: e.clientY - imagePosition.y })
    }
  }

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging && zoomLevel > 1) {
      setImagePosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      })
    }
  }

  const handleMouseUp = () => {
    setIsDragging(false)
  }

  const isImage = (extension: string) => {
    return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(extension)
  }

  // Load image with progress tracking using XMLHttpRequest
  const loadImageWithProgress = (url: string, fileName: string) => {
    setImageLoadStates(prev => ({...prev, [fileName]: 'loading'}))
    setLoadProgress(prev => ({...prev, [fileName]: 0}))

    const xhr = new XMLHttpRequest()
    
    // Track download progress
    xhr.addEventListener('progress', (event) => {
      if (event.lengthComputable) {
        const percentage = Math.round((event.loaded / event.total) * 100)
        setLoadProgress(prev => ({...prev, [fileName]: percentage}))
      } else {
        // If server doesn't send content-length, simulate progress
        const elapsed = Date.now() - startTime
        const simulatedProgress = Math.min(90, Math.floor(elapsed / 100)) // Max 90% simulated
        setLoadProgress(prev => ({...prev, [fileName]: simulatedProgress}))
      }
    })

    const startTime = Date.now()

    // On successful load
    xhr.addEventListener('load', () => {
      if (xhr.status === 200) {
        setLoadProgress(prev => ({...prev, [fileName]: 100}))
        
        const blob = xhr.response
        const objectUrl = URL.createObjectURL(blob)
        
        const img = new Image()
        img.onload = () => {
          setImageLoadStates(prev => ({...prev, [fileName]: 'loaded'}))
        }
        img.onerror = () => {
          URL.revokeObjectURL(objectUrl)
          handleImageError(fileName, url)
        }
        img.src = objectUrl
      } else {
        handleImageError(fileName, url)
      }
    })

    // On error
    xhr.addEventListener('error', () => {
      console.error(`❌ XHR error loading ${fileName}`)
      handleImageError(fileName, url)
    })

    // On timeout
    xhr.addEventListener('timeout', () => {
      console.error(`❌ XHR timeout loading ${fileName}`)
      handleImageError(fileName, url)
    })

    xhr.open('GET', url, true)
    xhr.responseType = 'blob'
    xhr.timeout = 30000 // 30 second timeout
    xhr.send()
  }

  // Handle image load
  const handleImageLoad = (fileName: string) => {
    setImageLoadStates(prev => ({...prev, [fileName]: 'loaded'}))
    setLoadProgress(prev => ({...prev, [fileName]: 100}))
  }

  // Handle image error with retry
  const handleImageError = (fileName: string, url: string) => {
    const currentRetries = retryCount[fileName] || 0
    
    if (currentRetries < 3) {
      // Retry up to 3 times
      console.log(`⚠️ Image failed to load: ${fileName}, retrying (${currentRetries + 1}/3)...`)
      setRetryCount(prev => ({...prev, [fileName]: currentRetries + 1}))
      
      // Force reload with progress tracking
      setTimeout(() => {
        loadImageWithProgress(`${url}?retry=${currentRetries + 1}&t=${Date.now()}`, fileName)
      }, 1000 * (currentRetries + 1)) // Increasing delay: 1s, 2s, 3s
    } else {
      console.error(`❌ Image failed to load after 3 retries: ${fileName}`)
      setImageLoadStates(prev => ({...prev, [fileName]: 'error'}))
      setLoadProgress(prev => ({...prev, [fileName]: 0}))
    }
  }

  // Preload current image with progress
  useEffect(() => {
    if (currentFile && isImage(currentFile.extension) && !imageLoadStates[currentFile.name]) {
      loadImageWithProgress(currentFile.url, currentFile.name)
    }
  }, [currentFile])

  // Set initial loading states and preload thumbnails
  useEffect(() => {
    const initialStates: ImageLoadState = {}
    files.forEach((file, index) => {
      if (isImage(file.extension)) {
        initialStates[file.name] = 'loading'
        
        // Preload thumbnails progressively (not all at once)
        setTimeout(() => {
          loadImageWithProgress(file.url, file.name)
        }, index * 200) // Stagger by 200ms each
      }
    })
    setImageLoadStates(initialStates)
  }, [files])

  if (files.length === 0 || !currentFile) {
    return (
      <div className='modern-file-viewer no-files'>
        <div className='no-files-message'>
          <i className='bi bi-file-earmark-x' style={{ fontSize: '4rem', color: '#64748b' }}></i>
          <h3>
            <FormattedMessage id='ERROR.NO_FILES' defaultMessage='No files available' />
          </h3>
          <p>
            <FormattedMessage
              id='ERROR.NO_FILES_DESC'
              defaultMessage='No files were provided to display.'
            />
          </p>
          <button className='btn btn-primary' onClick={() => window.close()}>
            <FormattedMessage id='MENU.CLOSE' defaultMessage='Close' />
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className={`modern-file-viewer ${isFullscreen ? 'fullscreen' : ''}`}>
      {/* Header */}
      <div className='viewer-header'>
        <div className='header-left'>
          <img
            src={toAbsoluteUrl('/media/logos/maarefati-logo.png')}
            alt='Maarefati'
            className='logo'
          />
          <div className='file-info'>
            <h2 className='file-name'>{currentFile.name}</h2>
            <span className='file-counter'>
              {currentIndex + 1} / {files.length}
            </span>
          </div>
        </div>

        <div className='header-right'>
          {/* Zoom Controls */}
          <div className='zoom-controls'>
            <button
              className='btn-icon'
              onClick={zoomOut}
              disabled={zoomLevel <= 0.5}
              title={intl.formatMessage({ id: 'MENU.ZOOM_OUT', defaultMessage: 'Zoom Out' })}
            >
              <i className='bi bi-dash-circle'></i>
            </button>
            <span className='zoom-level'>{Math.round(zoomLevel * 100)}%</span>
            <button
              className='btn-icon'
              onClick={zoomIn}
              disabled={zoomLevel >= 3}
              title={intl.formatMessage({ id: 'MENU.ZOOM_IN', defaultMessage: 'Zoom In' })}
            >
              <i className='bi bi-plus-circle'></i>
            </button>
            <button
              className='btn-icon'
              onClick={resetZoom}
              title={intl.formatMessage({ id: 'MENU.RESET_ZOOM', defaultMessage: 'Reset' })}
            >
              <i className='bi bi-arrows-angle-contract'></i>
            </button>
          </div>

          {/* Action Buttons */}
          <button
            className='btn-icon'
            onClick={toggleFullscreen}
            title={intl.formatMessage({
              id: 'MENU.FULLSCREEN',
              defaultMessage: 'Fullscreen',
            })}
          >
            <i className={`bi bi-${isFullscreen ? 'fullscreen-exit' : 'arrows-fullscreen'}`}></i>
          </button>

          <button
            className='btn-icon'
            onClick={handleDownload}
            title={intl.formatMessage({ id: 'MENU.DOWNLOAD', defaultMessage: 'Download' })}
          >
            <i className='bi bi-download'></i>
          </button>

          {files.length > 1 && (
            <button
              className='btn-icon'
              onClick={handleDownloadAll}
              title={intl.formatMessage({
                id: 'MENU.DOWNLOAD_ALL',
                defaultMessage: 'Download All',
              })}
            >
              <i className='bi bi-cloud-arrow-down'></i>
            </button>
          )}

          <button
            className='btn-icon btn-close-viewer'
            onClick={() => window.close()}
            title={intl.formatMessage({ id: 'MENU.CLOSE', defaultMessage: 'Close' })}
          >
            <i className='bi bi-x-lg'></i>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className='viewer-content'>
        {/* Thumbnail Sidebar */}
        {files.length > 1 && (
          <div className='thumbnail-sidebar'>
            {/* Scroll Up Button */}
            {currentIndex > 0 && (
              <button className='scroll-btn scroll-up' onClick={() => goToImage(currentIndex - 1)}>
                <i className='bi bi-chevron-up'></i>
              </button>
            )}

            {/* Thumbnails */}
            <div className='thumbnails-container'>
              {files.map((file, index) => (
                <div
                  key={index}
                  className={`thumbnail ${index === currentIndex ? 'active' : ''} ${imageLoadStates[file.name] === 'loading' ? 'loading' : ''}`}
                  onClick={() => goToImage(index)}
                >
                  {isImage(file.extension) ? (
                    <>
                      <img 
                        src={file.url} 
                        alt={file.name}
                        loading="lazy"
                        onLoad={() => handleImageLoad(file.name)}
                        onError={() => {
                          console.warn(`⚠️ Thumbnail failed: ${file.name}`)
                          setImageLoadStates(prev => ({...prev, [file.name]: 'error'}))
                        }}
                        style={{
                          opacity: imageLoadStates[file.name] === 'loaded' ? 1 : 0.3
                        }}
                      />
                      {imageLoadStates[file.name] === 'loading' && loadProgress[file.name] !== undefined && (
                        <div className='thumbnail-loading-overlay'>
                          <div className='mini-circular-progress'>
                            <svg width='40' height='40' viewBox='0 0 40 40'>
                              <circle
                                cx='20'
                                cy='20'
                                r='16'
                                fill='none'
                                stroke='rgba(255,255,255,0.3)'
                                strokeWidth='3'
                              />
                              <circle
                                cx='20'
                                cy='20'
                                r='16'
                                fill='none'
                                stroke='white'
                                strokeWidth='3'
                                strokeLinecap='round'
                                strokeDasharray={`${2 * Math.PI * 16}`}
                                strokeDashoffset={`${2 * Math.PI * 16 * (1 - (loadProgress[file.name] || 0) / 100)}`}
                                transform='rotate(-90 20 20)'
                              />
                              <text
                                x='20'
                                y='20'
                                textAnchor='middle'
                                dominantBaseline='middle'
                                style={{
                                  fontSize: '8px',
                                  fontWeight: 'bold',
                                  fill: 'white'
                                }}
                              >
                                {loadProgress[file.name] || 0}%
                              </text>
                            </svg>
                          </div>
                        </div>
                      )}
                      {imageLoadStates[file.name] === 'error' && (
                        <div className='thumbnail-placeholder' style={{background: '#fee2e2'}}>
                          <i className='bi bi-exclamation-triangle' style={{color: '#ef4444'}}></i>
                          <span style={{fontSize: '0.5rem', color: '#ef4444'}}>Error</span>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className='thumbnail-placeholder'>
                      <i className='bi bi-file-earmark'></i>
                      <span>{file.extension.toUpperCase()}</span>
                    </div>
                  )}
                  {index === currentIndex && <div className='thumbnail-indicator'></div>}
                </div>
              ))}
            </div>

            {/* Scroll Down Button */}
            {currentIndex < files.length - 1 && (
              <button className='scroll-btn scroll-down' onClick={() => goToImage(currentIndex + 1)}>
                <i className='bi bi-chevron-down'></i>
              </button>
            )}
          </div>
        )}

        {/* Main Display Area */}
        <div className='main-display'>
          {/* Navigation Arrows */}
          {files.length > 1 && (
            <>
              <button
                className='nav-arrow nav-prev'
                onClick={prevImage}
                disabled={currentIndex === 0}
              >
                <i className='bi bi-chevron-left'></i>
              </button>
              <button
                className='nav-arrow nav-next'
                onClick={nextImage}
                disabled={currentIndex === files.length - 1}
              >
                <i className='bi bi-chevron-right'></i>
              </button>
            </>
          )}

          {/* Image Display */}
          <div
            className='image-display'
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            style={{ cursor: zoomLevel > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default' }}
          >
            {isImage(currentFile.extension) ? (
              <>
                {/* Loading Spinner with Percentage */}
                {imageLoadStates[currentFile.name] === 'loading' && (
                  <div className='image-loading-overlay'>
                    {/* Circular Progress */}
                    <div className='circular-progress'>
                      <svg width='120' height='120' viewBox='0 0 120 120'>
                        {/* Background circle */}
                        <circle
                          cx='60'
                          cy='60'
                          r='54'
                          fill='none'
                          stroke='#e2e8f0'
                          strokeWidth='8'
                        />
                        {/* Progress circle */}
                        <circle
                          cx='60'
                          cy='60'
                          r='54'
                          fill='none'
                          stroke='#0ea5e9'
                          strokeWidth='8'
                          strokeLinecap='round'
                          strokeDasharray={`${2 * Math.PI * 54}`}
                          strokeDashoffset={`${2 * Math.PI * 54 * (1 - (loadProgress[currentFile.name] || 0) / 100)}`}
                          transform='rotate(-90 60 60)'
                          style={{transition: 'stroke-dashoffset 0.3s ease'}}
                        />
                        {/* Percentage text */}
                        <text
                          x='60'
                          y='60'
                          textAnchor='middle'
                          dominantBaseline='middle'
                          style={{
                            fontSize: '24px',
                            fontWeight: 'bold',
                            fill: '#1e293b'
                          }}
                        >
                          {loadProgress[currentFile.name] || 0}%
                        </text>
                      </svg>
                    </div>
                    <p className='mt-3 text-muted fw-bold'>
                      <FormattedMessage id='STATUS.LOADING_IMAGE' defaultMessage='Loading image...' />
                    </p>
                    <div className='progress mt-2' style={{width: '200px', height: '8px'}}>
                      <div 
                        className='progress-bar progress-bar-striped progress-bar-animated bg-primary' 
                        role='progressbar'
                        style={{width: `${loadProgress[currentFile.name] || 0}%`}}
                        aria-valuenow={loadProgress[currentFile.name] || 0}
                        aria-valuemin={0}
                        aria-valuemax={100}
                      ></div>
                    </div>
                  </div>
                )}

                {/* Error State */}
                {imageLoadStates[currentFile.name] === 'error' && (
                  <div className='file-preview-unsupported'>
                    <i className='bi bi-image' style={{ fontSize: '4rem', color: '#ef4444' }}></i>
                    <h3 style={{color: '#ef4444'}}>
                      <FormattedMessage id='ERROR.IMAGE_FAILED' defaultMessage='Failed to Load Image' />
                    </h3>
                    <p>
                      <FormattedMessage
                        id='ERROR.IMAGE_FAILED_DESC'
                        defaultMessage='The image could not be loaded. It may be corrupted or the connection is slow.'
                      />
                    </p>
                    <div className='d-flex gap-2 justify-content-center'>
                      <button 
                        className='btn btn-primary' 
                        onClick={() => {
                          setRetryCount(prev => ({...prev, [currentFile.name]: 0}))
                          setImageLoadStates(prev => ({...prev, [currentFile.name]: 'loading'}))
                          setTimeout(() => window.location.reload(), 100)
                        }}
                      >
                        <i className='bi bi-arrow-clockwise me-2'></i>
                        <FormattedMessage id='MENU.RETRY' defaultMessage='Retry' />
                      </button>
                      <button className='btn btn-secondary' onClick={handleDownload}>
                        <i className='bi bi-download me-2'></i>
                        <FormattedMessage id='MENU.DOWNLOAD' defaultMessage='Download' />
                      </button>
                    </div>
                  </div>
                )}

                {/* Actual Image */}
                <img
                  src={`${currentFile.url}${retryCount[currentFile.name] ? `?retry=${retryCount[currentFile.name]}&t=${Date.now()}` : ''}`}
                  alt={currentFile.name}
                  style={{
                    transform: `scale(${zoomLevel}) translate(${imagePosition.x / zoomLevel}px, ${
                      imagePosition.y / zoomLevel
                    }px)`,
                    transition: isDragging ? 'none' : 'transform 0.3s ease',
                    display: imageLoadStates[currentFile.name] === 'loaded' ? 'block' : 'none',
                  }}
                  draggable={false}
                  onLoad={() => handleImageLoad(currentFile.name)}
                  onError={() => handleImageError(currentFile.name, currentFile.url)}
                />
              </>
            ) : (
              <div className='file-preview-unsupported'>
                <i className='bi bi-file-earmark-text' style={{ fontSize: '4rem' }}></i>
                <h3>
                  {currentFile.extension.toUpperCase()}{' '}
                  <FormattedMessage id='ERROR.FILE_PREVIEW' defaultMessage='Preview Not Available' />
                </h3>
                <p>
                  <FormattedMessage
                    id='ERROR.DOWNLOAD_TO_VIEW'
                    defaultMessage='Download the file to view it.'
                  />
                </p>
                <button className='btn btn-primary' onClick={handleDownload}>
                  <i className='bi bi-download me-2'></i>
                  <FormattedMessage id='MENU.DOWNLOAD' defaultMessage='Download' />
                </button>
              </div>
            )}
          </div>

          {/* Image Info Overlay */}
          <div className='image-info-overlay'>
            <span className='image-name'>{currentFile.name}</span>
            <span className='image-size'>
              {files.length > 1 && `${currentIndex + 1}/${files.length}`}
            </span>
          </div>
        </div>
      </div>

      {/* Keyboard Shortcuts Help */}
      <div className='keyboard-shortcuts-hint'>
        <i className='bi bi-keyboard me-2'></i>
        <span>
          <FormattedMessage
            id='MENU.KEYBOARD_SHORTCUTS'
            defaultMessage='← → Navigate | + - Zoom | F Fullscreen | ESC Exit'
          />
        </span>
      </div>
    </div>
  )
}

